import {
  MultitipCreate,
  MultitipDetailed,
  PollCreate,
  PollDetailed,
  PostCreateInput,
  PostDetailed,
  TipCreate,
  TipDetailed,
} from '@app/graphql'

/**
 * Temporary demo-tenant feed isolation.
 *
 * On the demo tenant, user-created content (tips, multitips, posts, polls) must not
 * be published to the backend feed — otherwise it leaks onto the shared production
 * platform and stays there permanently.
 *
 * The create flows detect the demo tenant with `useIsDemoTenant()` (`demoProfileId`
 * on tenantConfig). Profile/inbox reads use `useDemoProfileTipsterId(tipsterId)`.
 * When on demo they SKIP the create mutation and instead build
 * the content object here and prepend it to the local feed state. The tester sees the
 * item immediately, but because nothing was persisted, a refresh reloads the feed from
 * the server and the item is gone.
 */

/** Minimal shape of the logged-in user needed to render a feed card. */
export interface DemoFeedUser {
  id: string
  username?: string
  name?: string
  surname?: string
  avatarUrl?: string
}

/** Rich tip state captured by the creation form (types/storage `Tip`). */
interface DemoTipState {
  eventId?: string
  eventDate?: Date | string | null
  sportId?: string
  competitionId?: string
  competitionName?: string
  marketId?: string
  marketName?: string
  outcomeId?: string
  outcomeName?: string
  odd?: number
  eventData?: Record<string, any> | null
}

const nowIso = () => new Date().toISOString()

/** Local ids are prefixed so they are easy to recognise and never collide with server ids. */
const localId = (): string => `demo-local-${Date.now()}-${Math.floor(Math.random() * 1e6)}`

const emptyVotes = () => ({
  __typename: 'Vote',
  upvoteCount: 0,
  downvoteCount: 0,
  upvoted: false,
  downvoted: false,
})

const buildUser = (user: DemoFeedUser) => ({
  __typename: 'UserPublic',
  id: user.id,
  displayName: user.username || `${user.name ?? ''} ${user.surname ?? ''}`.trim(),
  avatarUrl: user.avatarUrl || '',
})

/** Maps the form's captured event state onto the `event` field the feed card reads. */
const buildEvent = (tip?: DemoTipState) => {
  if (!tip || (!tip.eventId && !tip.eventData)) return null
  const ev = (tip.eventData ?? {}) as Record<string, any>
  return {
    __typename: 'SportEventDetailed',
    id: tip.eventId ?? ev.id ?? null,
    eventDate: tip.eventDate ?? ev.eventDate ?? null,
    sportId: tip.sportId ?? ev.sportId ?? null,
    categoryId: ev.categoryId ?? ev.category?.id ?? null,
    externalId: ev.externalId ?? null,
    externalCategoryId: ev.externalCategoryId ?? null,
    externalSportId: ev.externalSportId ?? null,
    externalTournamentId: ev.externalTournamentId ?? null,
    eventStatus: ev.eventStatus ?? null,
    eventType: ev.eventType ?? null,
    competitors: ev.competitors ?? [],
    tournament: ev.tournament ?? {
      __typename: 'Tournament',
      id: tip.competitionId ?? null,
      name: tip.competitionName ?? '',
      imageUrl: null,
      categoryId: null,
      externalId: null,
    },
    tournamentId: ev.tournamentId ?? tip.competitionId ?? null,
    oddType: {
      __typename: 'OddType',
      id: tip.marketId ?? null,
      name: tip.marketName ?? '',
      sportId: tip.sportId ?? null,
      externalId: null,
    },
    outcome: {
      __typename: 'Outcome',
      id: tip.outcomeId ?? null,
      name: tip.outcomeName ?? '',
      specialBetValue: null,
      bookmakerName: null,
    },
    createdAt: null,
    updatedAt: null,
    deletedAt: null,
  }
}

const buildTipCore = (params: {
  input: Partial<TipCreate> & { videoUrl?: string; audioUrl?: string }
  tip?: DemoTipState
  user: DemoFeedUser
  imageUrl?: string | null
  isPostTip?: boolean
}) => {
  const { input, tip, user, imageUrl, isPostTip } = params
  return {
    __typename: 'TipDetailed',
    contentType: 'tip',
    id: localId(),
    title: '',
    analysis: input.analysis ?? '',
    blocked: false,
    bookieId: input.bookieId ?? null,
    bookieImageUrl: null,
    customBookieName: input.customBookieName ?? '',
    channelContentId: null,
    commentsCount: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    deletedAt: null,
    event: buildEvent(tip),
    eventId: tip?.eventId ?? input.eventId ?? null,
    isCommentsAllow: input.isCommentsAllow ?? false,
    isMultitip: false,
    isSaved: false,
    multitip: null,
    odds: input.odds ?? tip?.odd ?? 0,
    outcomeId: input.outcomeId ?? tip?.outcomeId ?? null,
    profit: input.profit ?? null,
    result: null,
    stake: input.stake ?? 1,
    user: buildUser(user),
    userId: user.id,
    vip: input.vip ?? false,
    votes: emptyVotes(),
    sportId: input.sportId ?? tip?.sportId ?? null,
    imageUrl: imageUrl ?? null,
    videoUrl: input.videoUrl ?? null,
    audioUrl: input.audioUrl ?? null,
    streamUrl: null,
    coverImageUrl: null,
    isMultiPostTip: false,
    isPostTip: isPostTip ?? false,
    score: null,
  }
}

/** Builds a client-only single tip for the local demo feed. */
export function buildLocalTip(params: {
  input: Partial<TipCreate> & { videoUrl?: string; audioUrl?: string }
  tip?: DemoTipState
  user: DemoFeedUser
  imageUrl?: string | null
  isPostTip?: boolean
}): TipDetailed {
  return buildTipCore(params) as unknown as TipDetailed
}

/** Builds a client-only multitip for the local demo feed. */
export function buildLocalMultitip(params: {
  input: Partial<MultitipCreate>
  tips?: DemoTipState[]
  user: DemoFeedUser
}): MultitipDetailed {
  const { input, tips = [], user } = params
  const childTips = tips.map((tip, index) => {
    const child = buildTipCore({
      input: { analysis: input.analysis, vip: input.vip, odds: tip.odd },
      tip,
      user,
    })
    child.isMultitip = true
    child.title = `${index + 1}`
    return child
  })
  return {
    __typename: 'MultitipDetailed',
    contentType: 'multitip',
    id: localId(),
    title: '',
    analysis: input.analysis ?? '',
    blocked: false,
    bookieId: input.bookieId ?? null,
    bookieImageUrl: null,
    customBookieName: input.customBookieName ?? '',
    channelContentId: null,
    commentsCount: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    deletedAt: null,
    isCommentsAllow: input.isCommentsAllow ?? false,
    isSaved: false,
    odds: input.odds ?? 0,
    outcomeId: null,
    profit: input.profit ?? null,
    result: null,
    stake: input.stake ?? 1,
    tips: childTips,
    user: buildUser(user),
    userId: user.id,
    vip: input.vip ?? false,
    votes: emptyVotes(),
    streamUrl: null,
    videoUrl: null,
    audioUrl: null,
    coverImageUrl: null,
  } as unknown as MultitipDetailed
}

/** Builds a client-only post for the local demo feed. */
export function buildLocalPost(params: {
  input: Partial<PostCreateInput> & { mediaLink?: string }
  user: DemoFeedUser
}): PostDetailed {
  const { input, user } = params
  return {
    __typename: 'PostDetailed',
    contentType: 'post',
    id: localId(),
    category: input.category ?? 'sport',
    commentsCount: 0,
    content: input.content ?? '',
    createdAt: nowIso(),
    updatedAt: nowIso(),
    deletedAt: null,
    mediaLink: input.mediaLink ?? null,
    isCommentsAllow: input.isCommentsAllow ?? false,
    title: input.title ?? '',
    type: input.type ?? 'POST',
    user: buildUser(user),
    votes: emptyVotes(),
  } as unknown as PostDetailed
}

/** Builds a client-only poll for the local demo feed. */
export function buildLocalPoll(params: {
  input: Partial<PollCreate> & { options?: Array<{ option: string; position: number }> }
  user: DemoFeedUser
}): PollDetailed {
  const { input, user } = params
  const options = (input.options ?? []).map((opt) => ({
    __typename: 'PollOption',
    id: localId(),
    count: 0,
    option: opt.option,
    position: opt.position,
    pollId: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    deletedAt: null,
  }))
  return {
    __typename: 'PollDetailed',
    contentType: 'poll',
    id: localId(),
    category: input.category ?? 'event',
    commentsCount: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    deletedAt: null,
    expirationDate: input.expirationDate ?? null,
    isCommentsAllow: input.isCommentsAllow ?? false,
    options,
    question: input.question ?? '',
    user: buildUser(user),
    userSelectedOptionId: null,
    votes: emptyVotes(),
  } as unknown as PollDetailed
}
